import { supabase } from '../supabaseClient';

const SNAPSHOT_LIMIT = 25;

const toNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : null;
};

/**
 * Price/stock the user saw for a single part (an /api/enquire/ exact-match response).
 * qty_on_hand is only recorded when the user is allowed to see it, so the log
 * reflects what was actually on screen.
 */
export const buildShownSnapshot = (item, canViewFullStock = false) => {
  if (!item) return {};
  return {
    price_shown: toNumber(item.buy_price),
    list_price_shown: toNumber(item.base_price),
    qty_available_shown: toNumber(item.qty_available),
    qty_on_hand_shown: canViewFullStock ? toNumber(item.qty_on_hand) : null,
    unit_code: item.unit_code || null,
  };
};

/**
 * Parts returned by a multi-result search (fuzzy/description results or cross-references).
 * Price/stock are included when the source returned them.
 */
export const buildResultsSnapshot = (results) => {
  if (!Array.isArray(results) || results.length === 0) return null;
  return results.slice(0, SNAPSHOT_LIMIT).map((r) => ({
    part: r.prod_code || r.bom_code || null,
    description: r.Description || r.main_description || r.bom_description || null,
    price: toNumber(r.buy_price),
    qty_available: toNumber(r.qty_available),
  }));
};

/**
 * Log a customer search to customer_search_logs for the admin Search Analytics page.
 * Admin/staff searches are not logged. Never throws — logging must not break search.
 *
 * @param {object} params
 * @param {string} params.userId
 * @param {object} params.account - needs is_admin, is_staff, company_name, full_name
 * @param {string} params.searchTerm
 * @param {'exact'|'fuzzy'|'description'} params.searchType
 * @param {boolean} params.success
 * @param {number} [params.resultsCount]
 * @param {string|null} [params.partFound]
 * @param {string} params.source - part_enquiry | advanced_search | product_page | scanner
 * @param {object} [params.shown] - from buildShownSnapshot
 * @param {Array|null} [params.results] - from buildResultsSnapshot
 */
export const logCustomerSearch = async ({
  userId,
  account,
  searchTerm,
  searchType,
  success,
  resultsCount = 0,
  partFound = null,
  source,
  shown = {},
  results = null,
}) => {
  if (!userId || !account) return;
  if (account.is_admin === true || account.is_staff === true) return;

  try {
    const { error } = await supabase
      .from('customer_search_logs')
      .insert({
        user_id: userId,
        company_name: account.company_name,
        user_name: account.full_name,
        search_term: searchTerm,
        search_type: searchType,
        search_success: success,
        results_count: resultsCount,
        part_found: partFound,
        search_source: source,
        results_snapshot: results,
        ...shown,
      });

    if (error) {
      console.error('Error logging customer search:', error);
    }
  } catch (error) {
    console.error('Exception in logCustomerSearch:', error);
  }
};
